/**
 * Step 2 of the create-alias wizard: the display name and its visibility.
 *
 * Open to every tier, unlike the username: any character the general sanitizer
 * allows, subject to the same blocked-word list. Only the username is restricted,
 * because only the username is a globally unique claim worth squatting.
 */

import { useTranslation } from 'react-i18next';
import {
  DISPLAY_NAME_MAX_LENGTH,
  type NameCheckRejectionCode,
  type ProfileVisibility,
} from '@adieuu/shared';
import { Input } from '../../components/Input';
import { PrivacySelect } from '../../components/PrivacySelect';

export interface CreateDisplayNameStepProps {
  displayName: string;
  onDisplayNameChange: (value: string) => void;
  privacy: ProfileVisibility;
  onPrivacyChange: (value: ProfileVisibility) => void;
  /** Shown as the fallback others see when the display name is hidden. */
  usernameFallback: string;
  disabled?: boolean;
  rejection: NameCheckRejectionCode | null;
  checkFailed: boolean;
}

/** Maps a server verdict to its i18n key. */
function displayNameRejectionKey(code: NameCheckRejectionCode): string {
  switch (code) {
    case 'BLOCKED':
      return 'identity.create.errorDisplayNameBlocked';
    case 'TOO_SHORT':
      return 'identity.create.errorDisplayNameTooShort';
    case 'TOO_LONG':
      return 'identity.create.errorDisplayNameTooLong';
    // A display name cannot be taken, is not charset-restricted, and is never
    // required to be generated. Fall back to the generic message rather than
    // inventing copy for a verdict the server should not produce here.
    case 'TAKEN':
    case 'INVALID_CHARS':
    case 'NOT_GENERATED':
      return 'identity.create.errorValidation';
  }
}

export function CreateDisplayNameStep({
  displayName,
  onDisplayNameChange,
  privacy,
  onPrivacyChange,
  usernameFallback,
  disabled,
  rejection,
  checkFailed,
}: CreateDisplayNameStepProps) {
  const { t } = useTranslation();

  return (
    <div className="identity-wizard-step">
      <Input
        label={t('identity.create.displayNameLabel')}
        type="text"
        placeholder={t('identity.create.displayNamePlaceholder')}
        value={displayName}
        onChange={(e) => onDisplayNameChange(e.target.value)}
        disabled={disabled}
        maxLength={DISPLAY_NAME_MAX_LENGTH}
        autoFocus
        autoComplete="off"
        hint={t('identity.create.displayNameHint')}
        error={
          checkFailed
            ? t('identity.create.errorCheckFailed')
            : rejection
              ? t(displayNameRejectionKey(rejection))
              : undefined
        }
      />

      <div className="identity-displayname-privacy">
        <PrivacySelect
          value={privacy}
          onChange={onPrivacyChange}
          label={t('identity.create.displayNamePrivacyLabel')}
          disabled={disabled}
        />
        <p className="form-hint">
          {privacy === 'public' && (t('identity.create.displayNamePrivacyHintPublic'))}
          {privacy === 'friends' && (t('identity.create.displayNamePrivacyHintFriends', { displayName: displayName, username: usernameFallback }))}
          {privacy === 'private' && (t('identity.create.displayNamePrivacyHintHidden', { username: usernameFallback }))}
        </p>
      </div>
    </div>
  );
}
